import { getAccessMethod } from './access-method';

interface FetchOptions extends RequestInit {
  skipAuth?: boolean;
  useMockOnFailure?: boolean;
  mockData?: any;
}

// ── Helper: Get CSRF token from Django cookie (#37) ──
function getCSRFToken(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Utility function to make API calls with proper authentication and error handling
 * 
 * @param endpoint - The API endpoint to call (without the base URL)
 * @param options - Fetch options including mockData for fallback
 * @returns Promise with the response data or throws an error
 */
export async function fetchApi(endpoint: string, options: FetchOptions = {}) {
  const { 
    skipAuth = false, 
    useMockOnFailure = true, 
    mockData = null,
    ...fetchOptions 
  } = options;
  
  // No mock data usage
  
  try {
    // Determine base URL based on how we're being accessed
    const accessMethod = getAccessMethod();
    const baseUrl = accessMethod.baseUrl;
    
    // Construct URL properly, avoiding double /api/ paths
    let url: string;
    if (endpoint.startsWith('http')) {
      url = endpoint;
    } else {
      // Clean up endpoint to avoid double slashes
      const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      // If endpoint already starts with /api, don't add baseUrl
      if (cleanEndpoint.startsWith('/api')) {
        url = cleanEndpoint;
      } else {
        url = `${baseUrl}${cleanEndpoint}`;
      }
    }
    
    // Set default headers if not provided
    const headers = new Headers(options.headers || {});
    if (!headers.has('Content-Type') && options.body && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }
    // Always request JSON responses (#41)
    if (!headers.has('Accept')) {
      headers.set('Accept', 'application/json');
    }
    // Include Django CSRF token for mutating requests (#37)
    const method = (fetchOptions.method || 'GET').toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const csrfToken = getCSRFToken();
      if (csrfToken) {
        headers.set('X-CSRFToken', csrfToken);
      }
    }
    
    // For Django session authentication, we don't need JWT tokens
    // Just ensure credentials are included for cookies
    
    // Add tenant information from hostname if available (#43)
    try {
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        let tenant: string | null = null;
        if (hostname.includes('.localhost')) {
          // e.g. corbei.localhost → 'corbei'
          tenant = hostname.split('.')[0];
        } else if (hostname.includes('.') && !hostname.match(/^(www|app|localhost)/)) {
          tenant = hostname.split('.')[0];
        }
        if (tenant) {
          headers.set('x-tenant-subdomain', tenant);
        }
      }
    } catch (e) {
      // Silently ignore tenant header errors
    }
      // Make the request with timeout - use a longer timeout for development
    const controller = new AbortController();
    const timeoutDuration = process.env.NODE_ENV === 'development' ? 20000 : 15000; // 20s dev, 15s prod (#42)
    const timeoutId = setTimeout(() => {
      console.warn(`[API] Request timeout for ${endpoint} after ${timeoutDuration}ms`);
      controller.abort();
    }, timeoutDuration);
    
    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        credentials: 'include', // Include cookies for Django session authentication
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Check if response is OK
      if (!response.ok) {
        // Try to parse error message
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
        
        throw new Error(errorData.error || errorData.message || `API error: ${response.status} ${response.statusText}`);
      }
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      
      return await response.text();
    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }  } catch (error) {
    console.error(`API request failed: ${endpoint}`, error);
    
    // No mock data fallbacks - always throw the real error
    // This ensures we're always using the real API and never silently falling back to fake data
    throw error;
  }
}
